import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { getPortfolioApi } from '../api/portfolioApi';
import type { CreatePortfolioWorkPayload, PortfolioSolutionId, UpdatePortfolioWorkPayload } from '../types';

const errMsg = (err: unknown, fallback: string): string => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

// Root cache key; every query/mutation is additionally scoped by the resolved solution, since
// the same tenant only ever has one solution but the hooks are parameterised for reuse.
const KEY = 'portfolio';

export const usePortfolio = (tenantId: string | null | undefined, solutionId: PortfolioSolutionId | null) =>
  useQuery({
    queryKey: [KEY, solutionId, tenantId],
    queryFn: () => {
      if (!solutionId) return Promise.reject(new Error('No solution selected'));
      return getPortfolioApi(solutionId).list();
    },
    enabled: !!tenantId && !!solutionId,
    staleTime: 60 * 1000,
  });

export const useCreatePortfolioWork = (solutionId: PortfolioSolutionId | null) => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreatePortfolioWorkPayload) => {
      if (!solutionId) return Promise.reject(new Error('No solution selected'));
      return getPortfolioApi(solutionId).create(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, solutionId] });
      toast.success('Portfolio work created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create portfolio work')),
  });
};

export const useUpdatePortfolioWork = (solutionId: PortfolioSolutionId | null) => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePortfolioWorkPayload }) => {
      if (!solutionId) return Promise.reject(new Error('No solution selected'));
      return getPortfolioApi(solutionId).update(id, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, solutionId] });
      toast.success('Portfolio work updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update portfolio work')),
  });
};

export const useDeletePortfolioWork = (solutionId: PortfolioSolutionId | null) => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => {
      if (!solutionId) return Promise.reject(new Error('No solution selected'));
      return getPortfolioApi(solutionId).remove(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, solutionId] });
      toast.success('Portfolio work deleted');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to delete portfolio work')),
  });
};

export const useUploadPortfolioMedia = (solutionId: PortfolioSolutionId | null) => {
  const { toast } = useToast();
  return useMutation({
    mutationFn: (file: File) => {
      if (!solutionId) return Promise.reject(new Error('No solution selected'));
      return getPortfolioApi(solutionId).uploadMedia(file);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to upload media')),
  });
};
